/**
 * TemplateCard Component
 * Card view for a single template
 */

import { memo } from 'react';
import { MoreHorizontal, Copy, Pencil, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TEMPLATE_CATEGORIES, TEMPLATE_CHANNELS } from '@/constants/templateConstants';
import type { ITemplateCardProps } from '@/@types/messageTemplates';

// Format relative time - moved outside component to avoid impure function call during render
function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) return 'Today';
  if (days === 1) return '1 day ago';
  if (days < 30) return `${days} days ago`;
  if (days < 365) return `${Math.floor(days / 30)} months ago`;
  return `${Math.floor(days / 365)} years ago`;
}

function TemplateCard({ template, isOwner, onEdit, onDelete, onCopy }: ITemplateCardProps) {
  const categoryLabel =
    TEMPLATE_CATEGORIES.find(c => c.value === template.category)?.label || template.category;

  // Truncate content for preview
  const previewContent =
    template.content.length > 150 ? template.content.slice(0, 150) + '...' : template.content;

  return (
    <Card className="bg-card border-border hover:border-border-hover transition-colors">
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <Badge variant="secondary" className="bg-primary/10 text-primary border-0 text-xs">
            {categoryLabel}
          </Badge>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {isOwner ? (
                <>
                  <DropdownMenuItem onClick={() => onEdit(template)}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onCopy(template._id)}>
                    <Copy className="h-4 w-4 mr-2" />
                    Duplicate
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onDelete(template._id)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </>
              ) : (
                <DropdownMenuItem onClick={() => onCopy(template._id)}>
                  <Copy className="h-4 w-4 mr-2" />
                  Use Template
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Template Name */}
        <h3 className="font-medium text-foreground mb-2 line-clamp-1">{template.name}</h3>

        {/* Content Preview */}
        <p className="text-sm text-muted-foreground mb-4 line-clamp-3">{previewContent}</p>

        {/* Footer */}
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            {/* Channel badges */}
            <div className="flex gap-1">
              {template.channels.slice(0, 3).map(channel => {
                const channelLabel =
                  TEMPLATE_CHANNELS.find(c => c.value === channel)?.label || channel;
                return (
                  <span
                    key={channel}
                    className="px-1.5 py-0.5 bg-muted rounded text-[10px] uppercase"
                  >
                    {channelLabel.slice(0, 3)}
                  </span>
                );
              })}
              {template.channels.length > 3 && (
                <span className="px-1.5 py-0.5 bg-muted rounded text-[10px]">
                  +{template.channels.length - 3}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span>Used {template.usageCount} times</span>
            <span>{formatRelativeTime(template.updatedAt)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export const TemplateCardMemo = memo(TemplateCard);
